import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { renderNewplaAiMarkdown } from '../newpla-ai-chat/newpla-ai-markdown';
import { NewplaAiKnowledgeApi, NewplaAiKnowledgeDto, NewplaAiKnowledgeListDto } from './newpla-ai-knowledge.api';

type EditorTab = 'edit' | 'preview';

/**
 * 관리자 AI 설정. 지금은 사내 지식(매뉴얼 등) 관리. 나중에 다른 관리자 AI 설정을 탭으로 붙인다
 * - 지식은 글 또는 마크다운 글만 등록한다(파일 없음). AI는 지식 도구(knowledgeSearch·knowledgeRead)로 찾아 읽는다
 * - 긴 매뉴얼은 마크다운 제목(#, ##)으로 나눠 쓰면 AI가 필요한 제목만 읽는다
 * - 고치던 내용이 있으면 다른 지식을 열거나 새로 만들기 전에 묻는다. 창을 닫을 때도 묻는다
 *
 * 예: <newpla-ai-setting />
 */
@Component({
  selector: 'newpla-ai-setting',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './newpla-ai-setting.component.html',
  styleUrl: './newpla-ai-setting.component.scss',
})
export class NewplaAiSettingComponent implements OnInit {

  private api = inject(NewplaAiKnowledgeApi);
  private sanitizer = inject(DomSanitizer);

  /** 본문 최대 글자 수 (서버 NewplaAiKnowledgeService.CONTENT_MAX_LENGTH와 같다) */
  readonly contentMaxLength = 200_000;

  list: NewplaAiKnowledgeListDto[] = [];
  categories: string[] = [];
  keyword = '';
  categoryFilter = '';
  listLoading = false;

  form: NewplaAiKnowledgeDto | null = null;
  /** 열었을 때(또는 저장했을 때)의 폼. 고쳤는지 비교한다 */
  private original = '';
  tab: EditorTab = 'edit';
  previewHtml: SafeHtml | null = null;
  saving = false;
  errorText = '';
  savedText = '';

  private searchTimer?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    this.loadList();
    this.loadCategories();
  }

  // ================================================================ 목록

  async loadList(): Promise<void> {
    this.listLoading = true;
    try {
      this.list = await this.api.findList(this.keyword, this.categoryFilter);
    } finally {
      this.listLoading = false;
    }
  }

  async loadCategories(): Promise<void> {
    this.categories = await this.api.findCategories();
  }

  /** 검색어는 입력이 멈추면 찾는다 */
  onKeywordChange(): void {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadList(), 300);
  }

  // ================================================================ 편집

  get dirty(): boolean {
    return !!this.form && JSON.stringify(this.form) !== this.original;
  }

  async open(item: NewplaAiKnowledgeListDto): Promise<void> {
    if (this.form?.id === item.id || !this.confirmDiscard()) {
      return;
    }
    this.errorText = '';
    this.savedText = '';
    this.setForm(await this.api.get(item.id));
  }

  newKnowledge(): void {
    if (!this.confirmDiscard()) {
      return;
    }
    this.errorText = '';
    this.savedText = '';
    this.setForm({
      id: null, category: this.categoryFilter || null, title: '', summary: null, content: '', markdown: true, enabled: true,
    });
  }

  setTab(tab: EditorTab): void {
    this.tab = tab;
    if (tab === 'preview' && this.form) {
      this.previewHtml = this.form.markdown
        // 렌더러가 글 전체를 먼저 이스케이프하고 우리 태그만 붙인다 (newpla-ai-markdown)
        ? this.sanitizer.bypassSecurityTrustHtml(renderNewplaAiMarkdown(this.form.content))
        : null;
    }
  }

  async save(): Promise<void> {
    if (!this.form || this.saving) {
      return;
    }
    if (!this.form.title.trim() || !this.form.content.trim()) {
      this.errorText = '제목과 본문을 입력하세요.';
      return;
    }
    this.saving = true;
    this.errorText = '';
    try {
      const saved = this.form.id ? await this.api.update(this.form.id, this.form) : await this.api.create(this.form);
      this.setForm(saved, this.tab);
      this.savedText = '저장했습니다.';
      setTimeout(() => (this.savedText = ''), 2500);
      await Promise.all([this.loadList(), this.loadCategories()]);
    } catch (e) {
      this.errorText = errorMessage(e);
    } finally {
      this.saving = false;
    }
  }

  async remove(): Promise<void> {
    if (!this.form?.id || !confirm(`"${this.form.title}" 지식을 지울까요? AI가 더 이상 참고하지 못합니다.`)) {
      return;
    }
    try {
      await this.api.delete(this.form.id);
      this.form = null;
      this.original = '';
      await Promise.all([this.loadList(), this.loadCategories()]);
    } catch (e) {
      this.errorText = errorMessage(e);
    }
  }

  close(): void {
    if (this.confirmDiscard()) {
      this.form = null;
      this.original = '';
    }
  }

  /** Ctrl+S로 저장 */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's' && this.form) {
      event.preventDefault();
      this.save();
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.dirty) {
      event.preventDefault();
    }
  }

  trackById(_: number, k: NewplaAiKnowledgeListDto): string {
    return k.id;
  }

  private setForm(form: NewplaAiKnowledgeDto, tab: EditorTab = 'edit'): void {
    this.form = { ...form };
    this.original = JSON.stringify(this.form);
    this.setTab(tab);
  }

  private confirmDiscard(): boolean {
    return !this.dirty || confirm('저장하지 않은 내용이 있습니다. 버릴까요?');
  }
}

function errorMessage(e: unknown): string {
  if (e instanceof HttpErrorResponse) {
    const body = e.error;
    const msg = typeof body === 'string' ? body : body?.message ?? body?.msg;
    if (msg) {
      return msg;
    }
  }
  return '처리하지 못했습니다. 다시 시도하세요.';
}
